/**
 * esign.ts — the constants and small helpers every signing surface in this app shares.
 *
 * Three things live here that would otherwise be copied into four files and drift:
 *
 *   1. THE KILL SWITCH. `VITE_SIGNING_DISABLED` hides every signing CTA and closes both
 *      `/sign` routes. It is read once, at module load, because a flag that can change
 *      mid-session is a flag that can strand a half-finished ceremony.
 *   2. THE STORAGE PATH. `signatures/<scope>/<document_id>/<PARTY>-<uuid>.png`, where the
 *      SCOPE IS THE SERVER'S ANSWER and never one worked out here. It is fixed by SQL at
 *      two independent points — the bucket's INSERT policy (`can_write_signature_object`)
 *      and `agreement_sign`, which anchors the same shape and additionally requires
 *      segment 3 to be THIS document. Building it anywhere but here would mean two places
 *      to keep in step, and a path SQL refuses leaves an orphan PNG in the bucket.
 *   3. THE ERROR VOCABULARY. Six raised codes mean something specific to a signer and
 *      change what they should do next; everything else gets one calm sentence. See
 *      `signingErrorMessage`.
 */

/** Environment flag: `true` closes the signing routes and hides every CTA. */
export const SIGNING_DISABLED =
    String(import.meta.env.VITE_SIGNING_DISABLED ?? '').trim().toLowerCase() === 'true';

/**
 * The consent wording this app shows, versioned. Stored on every signature row as
 * `consent_text_version`, so a later change to the attestation text is provable against
 * what a given signer actually agreed to. BUMP THIS whenever the attestation copy below
 * changes in substance.
 *
 * ── v6, 2026-09-16: THE ADVANCE IS REPAID WEEKLY, AND SAYS SO ────────────────
 * The Operational Expense Advance's repayment attestation counted ROWS. `loan_instalments` is a
 * rate history — each row reads "from this Monday the weekly amount is X" — so two rows are one
 * change of rate and never two payments, and "I will repay this advance over 2 instalments." was
 * a sentence about the schema rather than about the document. The Schedule's money grid carries
 * two rows instead, Weekly repayment and First repayment, and the owner ruled (2026-09-16) that
 * every regular advance has exactly that: a weekly amount from a first Monday until it is
 * cleared [D3]. What a driver was ticking was therefore not merely oddly worded — on an advance
 * whose weekly figure had been revised the count was WRONG, and no count is a term of the
 * instrument either way.
 *   · ONE ATTESTATION WITHDRAWN, on the operational advance: "I will repay this advance over N
 *     instalments.", with the "Repaid over N instalments" / "First instalment X" pair above it.
 *   · ONE PUT IN ITS PLACE, in the grid's own terms: "I will repay X every Monday from D until
 *     this advance is repaid in full.", under "Repaid X a week" and "From D, every Monday until
 *     it is repaid in full". X is the weekly amount now in force — the latest `starts_at` — and D
 *     is the day the schedule began, the earliest, read as a calendar day and not as an instant.
 *     The term is stated only when both resolve; an advance with no schedule states neither.
 *   · THE ADVANCE ROW'S SUPPORTING LINE now names WHAT THE ADVANCE IS FOR, the Schedule's own
 *     `{{loan.purpose}}`, where the payload carries it. It had shown the disbursement
 *     TRANSACTION's bank narration, which is a different string the server publishes under a
 *     confusingly similar name and which the document being signed does not print anywhere.
 * Nothing else on either advance checklist moved, and no figure, rate or date changed meaning.
 *
 * ── v5, 2026-08-31: THE PREMIUM THE DRIVER PAID IN FULL ──────────────────────
 * The insurance checklist branched. A year in which the DRIVER paid the whole premium is not an
 * advance — clause 17.2 of the Advance Terms: "Where the Borrower pays that premium in full,
 * Primelane advances nothing, no interest arises" — and the owner ruled (2026-08-31) that such a
 * year still gets an instrument signed by both parties, the Insurance Premium Payment Record
 * [D-B45]. The loan row that carries one holds the WHOLE premium in `amount`, so the v4
 * sentences about it were not merely in the wrong register: a driver would have ticked
 * "Primelane has advanced GHS 5,632.00 of my premium" over a payment they made themselves.
 *   · FOUR NEW ATTESTATIONS, on that branch only. "The premium for this year is X."; "I paid
 *     this premium in full on D."; "Primelane advanced nothing towards this premium, so no
 *     interest arises and nothing is owed for this year."; and the cover row rewritten to "The
 *     policy this premium pays for runs W."
 *   · FOUR v4 SENTENCES WITHDRAWN there: the amount-advanced row, the up-front contribution row
 *     ("Deducted before interest is calculated", of a year in which no interest is calculated),
 *     the flat-rate row, and the cover row's "this advance" — an advance that does not exist.
 *   · ONE ADVANCE SENTENCE HARDENED, on every year rather than only that branch: the rate row
 *     now guards on the FIGURE rather than on its formatting, because `percent(0)` is the string
 *     "0%" and "Interest is 0%, flat" ticks a charge nobody makes.
 * The advance checklist is otherwise word for word v4's: a year Primelane did advance against
 * reads exactly as it did, and every figure, schedule and obligation is unmoved.
 *
 * ── v4, 2026-08-19: THE TWO ADVANCE CHECKLISTS SPEAK THE ADVANCE REGISTER ────
 * v3 renamed the DOCUMENTS; the sentences a driver ticks against them still said "loan". The
 * owner ruled the attestations follow the published bodies (2026-08-19), and the register is
 * part of what moved: Act 1052 s.85 already reaches Primelane as a lender without this app
 * volunteering "lent" and "borrowing", and what these two instruments record is money Primelane
 * paid out on the driver's behalf and recovers from collections, not cash it lent. Fourteen
 * strings moved, all of them in `agreement-key-terms.ts`:
 *   · OPERATIONAL EXPENSE ADVANCE, the whole checklist. "The loan" -> "The advance"; "You are
 *     borrowing X" -> "You were advanced X"; "I am borrowing X from Primelane." -> "Primelane
 *     has advanced me X."; "Interest is R of the amount lent" -> "of the amount advanced";
 *     "Charged once when the loan is made…" -> "when the advance is made…"; "Interest on this
 *     loan is R of the amount lent, charged once." -> "Interest on this advance is R of the
 *     amount advanced, charged once."; "I will repay this loan over N instalments." -> "this
 *     advance"; "The loan was advanced on D" -> "The advance was made on D"; "This loan was
 *     advanced to me on D." -> "This advance was made to me on D."
 *   · INSURANCE PREMIUM ADVANCE, five. "Interest on this insurance loan is R of the amount
 *     advanced, charged once." -> "Interest on this advance is R …", now word for word the
 *     operational one because clause 5.1 of the Advance Terms is ONE flat-interest rule over
 *     both; "The policy this loan pays for runs W." -> "The policy this advance pays for runs
 *     W."; and the premium row, which said FINANCING rather than lending and moved for the same
 *     reason — "The premium financed" -> "The amount advanced", "Primelane is financing X of
 *     insurance" -> "Primelane has advanced X of your premium", "Primelane is financing X of
 *     insurance for me." -> "Primelane has advanced X of my premium." Clause 1.1 names Primelane
 *     as the actor ("the part Primelane paid is the Amount advanced"); "financing" appears in
 *     neither Schedule nor the Terms.
 *   · NOTHING ELSE CARRIED EITHER WORD. The hire purchase, deposit and witness checklists and
 *     both ceremonies' own copy were swept: `DOC_TYPE_LABELS` and the
 *     AGREEMENT_PRIOR_SIGNATURE_REQUIRED sentence had already been brought to the advance names
 *     in v3, and no other string a driver ticks names a loan or calls an advance financing.
 * Every figure, schedule and obligation is the one v3 stated — only the noun and the verb moved.
 *
 * ── v3, 2026-08-18: THE REGISTRY'S OWN NAMES, AND TWO NEW THINGS TO ATTEST ───
 * The v1 registry published the family under names this app was not using, and the in-app
 * ceremony grew two steps, so a v3 signature stands for a wider set of words than a v2 one:
 *   · THE PRICE IS THE SCHEDULE'S PRICE. The hire purchase checklist confirmed a "Vehicle
 *     Purchase Price", a label the redraft retired family-wide; the Schedule's money grid
 *     names the figure "Hire-purchase price" and promises it does not change. A signer was
 *     ticking a figure under a name the document they had just read does not use.
 *   · THE TWO ADVANCES ARE NAMED AS THEY ARE PUBLISHED. "Loan Agreement" and "Insurance Loan
 *     Agreement" became "Operational Expense Advance" and "Insurance Premium Advance", so
 *     the documents card and the pending-signature banner call each instrument what its own
 *     title page calls it.
 *   · THE CEREMONY GAINED A DISCLOSURE STEP AND A TERMS STEP. Before the document opens, the
 *     Cash price and the Hire-purchase price are stated outside the agreement — which is what
 *     the Schedule's "What the Driver states to be true" says was done, and what NRCD 292 s.2
 *     requires. After the read, the Terms booklet clause 3 incorporates by form code, version
 *     and checksum is shown and accepted. Both are new things a signer attests to, and
 *     neither existed under v2.
 *
 * ── v2, 2026-08-13: THE ATTESTED SUBSTANCE MOVED WITH THE BODIES ─────────────
 * The agreement family was redrafted, and what a signer is asked to confirm changed with it:
 * the witness attestation NARROWED (identity and the information the driver gave, not
 * character or conduct, and "an understanding" rather than "full understanding"), the
 * witness gained a data-protection box under Act 843, and the deposit's refundability line
 * moved from "allocation or onboarding has started" to the driver ACCEPTING an allocated
 * vehicle. A v1 signature row therefore stands for different words from a v2 one, which is
 * exactly what this constant exists to record.
 *
 * NO VERSION-AWARE COPY MACHINERY SITS BEHIND THIS, deliberately. Production holds zero
 * agreement documents, so no live signature was ever taken against the v1 wording and there
 * is nothing to render two ways. Staging's handful of pre-pass test documents will show the
 * new ceremony copy over an old body until they are replaced — visible only to us, and
 * cheaper than a branch in the copy that would then have to be maintained forever.
 *
 * A BUMP DOES NOT REACH BACKWARDS, WHICH IS THE ONLY REASON THE COLUMN EXISTS.
 * `agreement_signatures.consent_text_version` is plain nullable text with no check constraint
 * and no foreign key: a stored row keeps the string it was signed under for good, and only
 * signatures taken after this line changes carry the new one. Staging holds three
 * `driver-app-consent-v3` rows from the walkthrough — a DEPOSIT and a HIRE_PURCHASE signed by
 * DRIVER, and an INSURANCE_LOAN signed by BORROWER, whose whole checklist is among the
 * fourteen v4 moves — plus one admin-side `admin-countersign-v3` and four unsigned rows still
 * null. None of them is orphaned by this; each remains provable against the wording above it.
 */
export const CONSENT_TEXT_VERSION = 'driver-app-consent-v6';

/** The bucket both ceremonies upload their drawn signature into. */
export const SIGNATURE_BUCKET = 'agreement-signatures';

/** localStorage namespace, per the app conventions. */
export const ESIGN_STORAGE_PREFIX = 'esign:';

// ─── Labels ──────────────────────────────────────────────────────────────────

/**
 * `agreement_templates.title` FOR EACH PUBLISHED TYPE, VERBATIM — not this app's own words
 * for them. The documents card titles both generations of a record from this map and the
 * pending-signature banner names the next document from it, while the driver is looking at a
 * document whose own title page carries the registry's string; a second name for the same
 * instrument reads as a second instrument.
 *
 * The two advances are where that bites. The v1 registry publishes LOAN as "Operational
 * Expense Advance" and INSURANCE_LOAN as "Insurance Premium Advance" — neither body calls
 * itself a loan agreement any more, and the words this map used to carry appear nowhere in
 * the document underneath them. PARTNER_MANAGEMENT has no published template of its own, so
 * it keeps a descriptive label rather than a quoted one.
 *
 * IT IS NOW THE FALLBACK AND NOT THE ANSWER — see `documentLabel`, which is what the surfaces
 * call. A doc type no longer names one instrument.
 */
export const DOC_TYPE_LABELS: Record<string, string> = {
    HIRE_PURCHASE: 'Hire Purchase Agreement',
    WITNESS: 'Witness Agreement',
    LOAN: 'Operational Expense Advance',
    INSURANCE_LOAN: 'Insurance Premium Advance',
    DEPOSIT: 'Deposit Agreement',
    PARTNER_MANAGEMENT: 'Partner Management Agreement',
};

/**
 * What to call a document on a card: ITS OWN TITLE, with the map above as the fallback.
 *
 * ── THE PRECEDENCE IS THE OTHER WAY ROUND FROM v1, DELIBERATELY ──────────────
 * `DOC_TYPE_LABELS` was the primary label because one doc type meant one published template.
 * INSURANCE_LOAN no longer does: a year Primelane advanced against is an "Insurance Premium
 * Advance", a year the driver paid in full is an "Insurance Premium Payment Record", and both
 * are raised under that same type [D-B45]. A label keyed on the type would call one of them by
 * the other's name — which is the failure the map was introduced to end, arriving from the
 * other side.
 *
 * THE SERVER ALREADY SENDS THE ANSWER. `agreement_render`, `get_agreement_documents` and
 * `get_my_pending_signatures` all project `title`, and it is the TEMPLATE's own — the string
 * printed on the title page the driver has open. The map stays behind it for a row that
 * arrives without one, and for the paper cards, which have no document to take a title from.
 */
export function documentLabel(title: string | null | undefined, docType: string): string {
    const named = typeof title === 'string' ? title.trim() : '';
    if (named.length > 0) return named;
    return DOC_TYPE_LABELS[docType] ?? 'Agreement';
}

export const PARTY_LABELS: Record<string, string> = {
    DRIVER: 'You',
    BORROWER: 'You',
    WITNESS: 'Witness',
    COMPANY_REP: 'Primelane',
    PARTNER: 'Partner',
};

/** How a party chip reads on a card. `role_label` from the template wins when present. */
export function partyLabel(party: string, roleLabel?: string | null): string {
    return PARTY_LABELS[party] ?? roleLabel ?? party;
}

/** The parties a driver session can ever be asked to sign as. */
export const MY_PARTIES = ['DRIVER', 'BORROWER'] as const;

export function isMyParty(party: string | null | undefined): boolean {
    return party === 'DRIVER' || party === 'BORROWER';
}

// ─── "Is this one waiting for me?" ───────────────────────────────────────────

/** Document statuses in which a signature can still be taken. */
export const OPEN_DOCUMENT_STATUSES = ['PENDING_REVIEW', 'IN_SIGNING'];

/** Signature-row statuses that mean the party has not signed yet. */
export const AWAITING_SIGNATURE_STATUSES = ['PENDING', 'SENT', 'VIEWED'];

/** Structural shape of one `get_agreement_documents` row, as this predicate reads it. */
export interface SignableDocument {
    status: string;
    signatures: readonly { party: string; status: string | null }[] | null;
}

/**
 * Whether THIS driver still owes THIS document a signature.
 *
 * One definition, because two of them drift: the Documents card decides whether to offer
 * "Review & Sign" with it, and the section badge counts with it. A badge that counted a
 * different set from the cards underneath it would send a driver looking for work that is
 * not there.
 */
export function awaitingMySignature(document: SignableDocument): boolean {
    if (SIGNING_DISABLED) return false;
    if (!OPEN_DOCUMENT_STATUSES.includes(document.status)) return false;
    const mine = (document.signatures ?? []).find((row) => isMyParty(row.party));
    return !!mine && AWAITING_SIGNATURE_STATUSES.includes(mine.status ?? '');
}

// ─── "Has the witness signed?" ───────────────────────────────────────────────

/** What the witness predicate reads. `AgreementDocumentSummary` satisfies it. */
export interface WitnessableDocument {
    doc_type: string;
    signatures: readonly { party: string; status: string | null }[] | null;
}

/**
 * Whether the WITNESS seat ON THIS DOCUMENT has been signed.
 *
 * ── IT IS A GATE, AND IT FAILS CLOSED ─────────────────────────────────────────
 * The owner's ruling (2026-08-21): a driver may see the witness agreement only AFTER the
 * witness has signed it. The document is frozen when the witness's link is issued — BEFORE
 * they sign — so its text exists, and is readable through the ordinary row policy, for the
 * whole window in which the answer to this question is "no". A roster that could not be read
 * therefore answers `false` rather than falling through: a predicate that opens on missing
 * evidence is not a gate.
 *
 * ── THE SEAT, NOT THE DOCUMENT'S STATUS ───────────────────────────────────────
 * `COMPLETED` would be the tempting test and it is the wrong one in both directions: a
 * witness agreement is COMPLETED only once Primelane has countersigned as well, which leaves
 * a window where the witness HAS signed and the driver is refused; and a status is a summary
 * of several seats, while the ruling is about one of them.
 *
 * fleet-backend enforces the same rule server-side in a later PR. Until then this carries it
 * alone, which is why it lives beside `awaitingMySignature` rather than inside a component.
 */
export function witnessSeatSigned(document: WitnessableDocument): boolean {
    const seat = (document.signatures ?? []).find((row) => row.party === 'WITNESS');
    return seat?.status === 'SIGNED';
}

/** What "can this be opened in the at-rest viewer?" reads. */
export interface ViewableDocumentSummary extends WitnessableDocument {
    status: string;
    finalized_at: string | null;
}

/**
 * Whether a card may offer "View agreement" — the route to `/agreement/:documentId`.
 *
 * A WITNESS agreement is viewable from the moment its witness has signed, executed or not:
 * before that there is nothing a driver is entitled to see, and after it the viewer shows
 * the frozen text until the sealed PDF exists. Every other instrument is viewable once it is
 * FINALIZED — before that the driver's own ceremony is where the text lives, and the card
 * still routes there.
 */
export function agreementViewable(document: ViewableDocumentSummary): boolean {
    if (document.status === 'VOIDED' || document.status === 'SUPERSEDED') return false;
    if (document.doc_type === 'WITNESS') return witnessSeatSigned(document);
    return !!document.finalized_at;
}

// ─── "Why is there no button on this card?" ──────────────────────────────────

/** What the inaction sentence reads. `AgreementDocumentSummary` satisfies it. */
export interface InactionDocument extends SignableDocument {
    doc_type: string;
}

/** Whether this driver holds a seat on the document at all — signed or not. */
function seatsMe(document: SignableDocument): boolean {
    return (document.signatures ?? []).some((row) => isMyParty(row.party));
}

/**
 * The sentence a card shows when it offers no action at all.
 *
 * HERE RATHER THAN BESIDE THE CARD because it is a classification, in the same family as
 * `awaitingMySignature` — the two decide between them what a card says, and a sentence that
 * claims the wrong thing renders perfectly.
 *
 * ── THE WITNESS AGREEMENT IS A CARD NOBODY COULD SEE BEFORE ──────────────────
 * `get_agreement_documents` returns the whole contract's documents, and the witness agreement
 * is the first live one on it that carries NO SEAT FOR THE DRIVER. It therefore lands under
 * "Agreements to sign" — a heading that asks for a signature — with no button and, until this
 * branch existed, "Primelane is still preparing this document." Which is false twice over on
 * a document that is out for signature: it is prepared, and it is not the driver's to sign.
 *
 * IN_SIGNING SPECIFICALLY, not "live". A PENDING_REVIEW witness agreement genuinely is still
 * being prepared — no link has been issued and there is nobody to wait on yet — so it keeps
 * the sentence that is true of it.
 *
 * ── AND THE SENTENCE NOW OUTLIVES `IN_SIGNING` ────────────────────────────────
 * The witness agreement became VIEWABLE once its witness has signed, so the card that used to
 * be actionless forever now grows an action at a moment in time. Which means the sentence has
 * to be true right up to that moment and not one status earlier: a witness agreement that has
 * moved on from IN_SIGNING with its WITNESS seat still unsigned used to fall through to
 * "Primelane is still preparing this document", which is exactly the false sentence this
 * branch was written to end. The test is now the SEAT — the same one `agreementViewable`
 * opens the card on — with PENDING_REVIEW keeping the sentence that is true of it.
 */
export function inactionReason(document: InactionDocument): string {
    if (document.status === 'VOIDED') return 'Primelane withdrew this document.';
    if (document.status === 'SUPERSEDED') return 'Primelane replaced this with a newer version.';
    if (SIGNING_DISABLED) return 'Signing is temporarily unavailable.';
    if (
        document.doc_type === 'WITNESS' &&
        document.status !== 'PENDING_REVIEW' &&
        !seatsMe(document) &&
        !witnessSeatSigned(document)
    ) {
        return 'Your witness signs this one.';
    }
    return 'Primelane is still preparing this document.';
}

// ─── "To sign" versus "already dealt with" ───────────────────────────────────

/**
 * Whether a document can still move — it is being prepared, or it is being signed.
 *
 * DELIBERATELY NOT `awaitingMySignature`. That predicate answers "is there work for ME",
 * and it is false while the driver waits on Primelane's countersignature or while the kill
 * switch is on. A document in either of those states is still a live one, and grouping by
 * the narrower predicate would drop it out of the to-sign list the moment the driver signed
 * — into a heading that says the thing is finished, which it is not.
 */
function isLiveDocument(document: { status: string }): boolean {
    return OPEN_DOCUMENT_STATUSES.includes(document.status);
}

/** The columns the grouping reads. `AgreementDocumentSummary` satisfies it. */
export interface GroupableDocument {
    status: string;
    created_at: string;
    completed_at: string | null;
}

export interface AgreementDocumentGroups<T> {
    /** Still in flight: "Agreements to sign". */
    live: T[];
    /** Finished or abandoned: "Signed & past agreements". Completed newest first, then the rest. */
    past: T[];
}

/** An unparseable or absent timestamp sorts last rather than throwing the whole order out. */
function parseTime(value: string | null | undefined): number {
    const parsed = Date.parse(value ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
}

/** Newest first, by whichever timestamp the document actually has. */
function documentTime(document: GroupableDocument): number {
    return parseTime(document.completed_at ?? document.created_at);
}

/**
 * Splits the Documents list into the two things a driver reads it for.
 *
 * THE ASYMMETRY IS THE POINT. `live` is an ALLOWLIST — only the two statuses in which a
 * signature can still be taken may sit under a heading that asks for one, so a status this
 * app has not met yet can never fake an action. `past` is then EVERYTHING ELSE, not a second
 * allowlist of COMPLETED and VOIDED: a document must never disappear from a driver's page
 * because a status was added upstream, and SUPERSEDED already exists to prove the case.
 */
export function groupAgreementDocuments<T extends GroupableDocument>(
    documents: readonly T[] | null | undefined,
): AgreementDocumentGroups<T> {
    const { live, completed, closed } = splitAgreementDocuments(documents);
    return { live, past: [...completed, ...closed] };
}

/** The three buckets `groupAgreementDocuments` and the unified list are both built from. */
function splitAgreementDocuments<T extends GroupableDocument>(
    documents: readonly T[] | null | undefined,
): { live: T[]; completed: T[]; closed: T[] } {
    const live: T[] = [];
    const completed: T[] = [];
    const closed: T[] = [];

    for (const document of documents ?? []) {
        if (isLiveDocument(document)) live.push(document);
        else if (document.status === 'COMPLETED') completed.push(document);
        else closed.push(document);
    }

    const newestFirst = (a: T, b: T) => documentTime(b) - documentTime(a);
    completed.sort(newestFirst);
    closed.sort(newestFirst);

    // `live` keeps the server's order — `get_agreement_documents` already returns
    // `created_at DESC, id DESC`, so the newest thing asked of the driver is at the top and a
    // re-sort here would only be a second opinion to keep in step.
    return { live, completed, closed };
}

// ─── One list, two generations of the same record ────────────────────────────

/**
 * THE DEFECT THIS EXISTS FOR. The Documents page grew a second generation without losing the
 * first, so a contract whose hire purchase agreement was signed IN THE APP printed it twice:
 * once as an in-app card ("Download your copy") and once, further down, as a legacy card
 * ("Signed" badge, "View Agreement") — the SAME PDF, because `agreement_finalize` writes its
 * URL back onto the legacy row it was generated for.
 *
 * The two are not two records. They are one record seen through two reads, and the thing that
 * says so is THE ARC: the exclusive foreign key on `agreement_documents` naming the legacy row
 * a document was raised against. So the page is built by arc, and a legacy row whose arc holds
 * an in-app document is not rendered at all — the in-app card is the canonical one.
 *
 * ── WHY THE KEY IS DERIVED HERE RATHER THAN READ ─────────────────────────────
 * `get_agreement_documents` projects no arc columns (the admin board reads
 * `agreement_documents` a second time to get them). This app does not need to, because both
 * arcs it shows are already identified by what the RPC does return:
 *
 *   contract arc   `uq_contract_agreements_contract_type` makes (contract, type) unique, and
 *                  `agreement_create` maps HIRE_PURCHASE -> DRIVER and WITNESS -> WITNESS. One
 *                  contract therefore has at most one legacy row per doc type, and the doc type
 *                  IS the arc. (This is exactly the reasoning the admin board rejects for
 *                  DEPOSIT/LOAN, where one contract holds several subjects of a type.)
 *   deposit arc    `path_scope` is `deposit/<contract_deposit_id>`, derived server-side by
 *                  `agreement_path_scope` — arc first, contract never consulted — and the
 *                  insights row carries `contract_deposit_id`. An exact join, per deposit.
 *
 * Loan and insurance-loan documents get no arc key: this page has never listed their legacy
 * rows, so there is nothing of theirs to deduplicate against.
 */

/** `agreement_create`'s own HIRE_PURCHASE/WITNESS -> DRIVER/WITNESS mapping. */
const CONTRACT_ARC_TYPE_BY_DOC_TYPE: Record<string, string> = {
    HIRE_PURCHASE: 'DRIVER',
    WITNESS: 'WITNESS',
};

/** The same mapping read backwards, so a scan is titled what its in-app twin would be. */
const DOC_TYPE_BY_CONTRACT_ARC_TYPE: Record<string, string> = {
    DRIVER: 'HIRE_PURCHASE',
    WITNESS: 'WITNESS',
};

/** The deposit half of `agreement_path_scope`. The other half is a bare contract id. */
const DEPOSIT_SCOPE = /^deposit\/([0-9]+)$/;

/** The columns an arc key is derived from. */
export interface ArcedDocument {
    doc_type: string;
    path_scope: string | null;
}

export function contractArcKey(legacyType: string): string {
    return `contract:${legacyType}`;
}

export function depositArcKey(contractDepositId: number | string): string {
    return `deposit:${contractDepositId}`;
}

/** The arc an in-app document was raised against, or null where this page shows no twin. */
export function documentArcKey(document: ArcedDocument): string | null {
    const legacyType = CONTRACT_ARC_TYPE_BY_DOC_TYPE[document.doc_type];
    if (legacyType) return contractArcKey(legacyType);

    if (document.doc_type === 'DEPOSIT') {
        const depositId = DEPOSIT_SCOPE.exec(document.path_scope ?? '')?.[1];
        return depositId ? depositArcKey(depositId) : null;
    }

    return null;
}

/**
 * Whether a document OCCUPIES its arc — a denylist of the two statuses that release it.
 *
 * The same rule the partial unique index behind every arc is keyed on
 * (`status NOT IN ('VOIDED','SUPERSEDED')`), and a denylist for the reason that index is one:
 * a withdrawn or replaced document binds nobody, so the legacy row underneath it is free to be
 * signed on paper again — and the admin board offers "Record paper signing" only on an arc in
 * exactly that state. Written as an allowlist, a status added upstream would quietly stop
 * suppressing its own duplicate and the page would print the record twice again.
 */
export function holdsArc(document: { status: string }): boolean {
    return document.status !== 'VOIDED' && document.status !== 'SUPERSEDED';
}

/** One row of the insights payload's `agreements[]` — a scanned contract agreement. */
export interface LegacyContractAgreement {
    /** `contract_agreements.type`: DRIVER or WITNESS. */
    type: string;
    signed_at: string | null;
    agreement_url: string | null;
}

/** One row of the insights payload's `deposit_agreements[]`. */
export interface LegacyDepositAgreement {
    id: number;
    contract_deposit_id: number;
    signed_at: string | null;
    agreement_url: string | null;
    deposit_amount: number;
    deposit_date: string;
}

/** A signed record that exists only as a scan — no in-app document was ever raised for it. */
export interface PaperAgreement {
    /** React key, and the identity a test asserts on. */
    key: string;
    arcKey: string;
    /** Whose title this card takes, so both generations of a record read the same. */
    docType: string;
    /** Never null — an unsigned legacy row is not a signed agreement, and has no card. */
    signedAt: string;
    /** The scan. Null where the row carries a date but no file was attached. */
    url: string | null;
    /** Deposit cards keep the money and the paid date they have always shown. */
    depositAmount: number | null;
    depositDate: string | null;
}

/** One card under "Signed agreements", from whichever generation it came. */
export type SignedAgreement<T> =
    | { kind: 'document'; key: string; document: T }
    | { kind: 'paper'; key: string; paper: PaperAgreement };

export interface UnifiedAgreements<T> {
    /** "Agreements to sign" — live in-app documents only, exactly as before. */
    live: T[];
    /** "Signed agreements" — one section, both generations, deduped by arc. */
    signed: SignedAgreement<T>[];
}

/** What the unified list needs off a `get_agreement_documents` row. */
export interface UnifiableDocument extends GroupableDocument, ArcedDocument {
    id: number;
}

export interface UnifiedAgreementsInput<T> {
    documents: readonly T[] | null | undefined;
    contractAgreements: readonly LegacyContractAgreement[] | null | undefined;
    depositAgreements: readonly LegacyDepositAgreement[] | null | undefined;
}

/**
 * The whole Documents page's agreement half, in one pure function.
 *
 * `live` is untouched — the to-sign heading only ever listed in-app documents and a scan is
 * nothing anybody can be asked to sign. `signed` merges what is settled: completed in-app
 * documents and undeduplicated scans interleaved newest first, then the withdrawn and replaced
 * documents, which keep their badges rather than disappearing.
 */
export function unifyAgreementDocuments<T extends UnifiableDocument>(
    input: UnifiedAgreementsInput<T>,
): UnifiedAgreements<T> {
    const { live, completed, closed } = splitAgreementDocuments(input.documents);

    // Every arc an in-app document currently occupies. Built from the WHOLE list, not from one
    // bucket: a live document suppresses its legacy twin just as a completed one does — the
    // record is being signed in the app, and the row underneath it is that same record's
    // placeholder, not a second agreement.
    const occupied = new Set<string>();
    for (const document of input.documents ?? []) {
        if (!holdsArc(document)) continue;
        const key = documentArcKey(document);
        if (key) occupied.add(key);
    }

    const paper: PaperAgreement[] = [];

    for (const row of input.contractAgreements ?? []) {
        if (!row.signed_at) continue;
        const arcKey = contractArcKey(row.type);
        if (occupied.has(arcKey)) continue;
        const docType = DOC_TYPE_BY_CONTRACT_ARC_TYPE[row.type];
        if (!docType) continue;
        paper.push({
            key: `paper-${arcKey}`,
            arcKey,
            docType,
            signedAt: row.signed_at,
            url: row.agreement_url,
            depositAmount: null,
            depositDate: null,
        });
    }

    for (const row of input.depositAgreements ?? []) {
        if (!row.signed_at) continue;
        const arcKey = depositArcKey(row.contract_deposit_id);
        if (occupied.has(arcKey)) continue;
        paper.push({
            key: `paper-${arcKey}`,
            arcKey,
            docType: 'DEPOSIT',
            signedAt: row.signed_at,
            url: row.agreement_url,
            depositAmount: row.deposit_amount,
            depositDate: row.deposit_date,
        });
    }

    const documentEntry = (document: T): SignedAgreement<T> => ({
        kind: 'document',
        key: `doc-${document.id}`,
        document,
    });

    // The sort key rides BESIDE the entry rather than on it: a scan is dated by its signature
    // and a document by its completion, and the two are only comparable here.
    const settled = [
        ...completed.map((document) => ({ at: documentTime(document), entry: documentEntry(document) })),
        ...paper.map((item) => ({
            at: parseTime(item.signedAt),
            entry: { kind: 'paper', key: item.key, paper: item } as SignedAgreement<T>,
        })),
    ];
    settled.sort((a, b) => b.at - a.at);

    return {
        live,
        signed: [...settled.map((row) => row.entry), ...closed.map(documentEntry)],
    };
}

// ─── Storage path ────────────────────────────────────────────────────────────

/**
 * `<contract_id>` or `deposit/<contract_deposit_id>` — and nothing else may enter a path.
 *
 * The same shape the public broker re-checks in `sign-agreement-public/validation.ts`. The
 * string comes from the database and the database is trusted; it is re-checked because it
 * is INTERPOLATED INTO AN OBJECT NAME, and a builder that would splice `undefined` or a
 * traversal into a bucket key the day something upstream changes is not worth two lines.
 */
const SCOPE_SHAPE = /^(?:deposit\/)?[0-9]+$/;

/**
 * `signatures/<scope>/<document_id>/<PARTY>-<uuid>.png`, byte-compatible with the anchored
 * regexes in `can_write_signature_object` and `agreement_sign`. The party name is stripped
 * to the characters those regexes accept.
 *
 * ── THE SCOPE IS THE SERVER'S, AND THAT IS THE WHOLE POINT ────────────────────
 * `path_scope` arrives on `agreement_render`, `get_agreement_documents` and
 * `get_my_pending_signatures`, derived by `agreement_path_scope` — which tests the DEPOSIT
 * ARC FIRST and does not consult `contract_id` when the arc answers. The rule that suggests
 * itself here — "use the contract this document names" — is a DIFFERENT rule, and the two
 * disagree on an ordinary document: a deposit agreement created for a deposit that has
 * ALREADY been assigned carries both a contract and an arc, and its driver signs IN SESSION
 * on this very page. SQL files that signature under `deposit/<contract_deposit_id>/…`;
 * composing `<contract_id>/…` here uploads a PNG the bucket accepts (the folder resolves to
 * a contract the driver is on) and `agreement_sign` then refuses with
 * AGREEMENT_SIGNATURE_PATH_INVALID — an orphan object in a private bucket and a driver who
 * cannot sign at all.
 *
 * NULL IS AN ANSWER, NOT AN ERROR TO ROUTE AROUND. A document scoped by neither a contract
 * nor a deposit has nowhere its objects could go — a row shape SQL's own
 * `agreement_documents_contract_or_deposit_arc` CHECK forbids — so refusing BEFORE the
 * upload is the difference between an early rejection and an orphan at a path SQL rejects.
 */
export function signatureObjectPath(
    pathScope: string | null | undefined,
    documentId: number,
    party: string,
    uuid: string,
): string | null {
    if (typeof pathScope !== 'string' || !SCOPE_SHAPE.test(pathScope)) return null;
    const safeParty = String(party).replace(/[^A-Za-z0-9]/g, '').toUpperCase() || 'PARTY';
    return `signatures/${pathScope}/${documentId}/${safeParty}-${uuid}.png`;
}

/** `crypto.randomUUID` is not in every mobile browser this app has to serve. */
export function randomUuid(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const rand = (Math.random() * 16) | 0;
        const value = ch === 'x' ? rand : (rand & 0x3) | 0x8;
        return value.toString(16);
    });
}

// ─── Hashing ─────────────────────────────────────────────────────────────────

/**
 * SHA-256 of a string as lowercase hex — the same digest SQL computes over
 * `rendered_md`, used ONLY as a cross-check that the bytes on screen are the bytes the
 * server hashed. The hash submitted with a signature is always the server's own
 * `content_sha256`, never this one.
 *
 * Returns null where `crypto.subtle` is unavailable (an insecure context), because a
 * missing cross-check must not become a reason a driver cannot sign.
 */
export async function sha256Hex(value: string): Promise<string | null> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    try {
        const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
        return Array.from(new Uint8Array(digest))
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return null;
    }
}

// ─── Images ──────────────────────────────────────────────────────────────────

/** `data:image/png;base64,…` -> the bytes the bucket wants. */
export function dataUrlToBlob(dataUrl: string): Blob {
    const [header, base64] = dataUrl.split(',');
    const mime = /data:([^;]+)/.exec(header)?.[1] ?? 'image/png';
    const binary = atob(base64 ?? '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

// ─── The error vocabulary ────────────────────────────────────────────────────

/**
 * The raised codes a signer is entitled to see as themselves.
 *
 * SQL raises these only AFTER the caller has proven they may sign, so surfacing them
 * leaks nothing — and withholding them is what actually harms: a driver whose tab went
 * stale would be told "something went wrong" and would retry the same stale hash forever.
 *
 * `AGREEMENT_PRIOR_SIGNATURE_REQUIRED` is the fifth, and the only one that is not a
 * statement about the document on screen. fleet-backend's `20260818030000` holds the
 * Insurance Premium Advance shut until the driver has signed the hire purchase agreement it
 * is made in connection with [D-B26], and the refusal lands on the ONE arm this app reaches:
 * the borrower signing in their own session. It is the worst of all failures to hide behind
 * "please try again" — no retry can move it, and the step that can is a different document
 * already sitting in the signer's own list. The public link path cannot raise it here:
 * `sign-agreement-public` passes three codes through and this is not one of them.
 *
 * `AGREEMENT_INSTRUMENT_MISMATCH` is the sixth, and it is a statement about the document that
 * cannot be true of the driver's own conduct. `agreement_sign` re-asserts, on both arms, that
 * the insurance year still has the money shape the document was drawn for [D-B45]: a Record
 * whose premium has stopped being fully paid, or has gained an instalment, is a page whose
 * particulars are no longer true, and it must not take a signature. Nothing the signer can do
 * moves it — the fix is a document Primelane reissues — so the sentence says that rather than
 * inviting a retry into a call that has already answered. It is inert until fleet-backend's
 * own PR lands; it ships first so that no driver ever meets it as a raw SQL string.
 */
export const PASSTHROUGH_CODES = [
    'AGREEMENT_CONTENT_CHANGED',
    'AGREEMENT_TYPED_NAME_REQUIRED',
    'AGREEMENT_SIGN_ORDER',
    'AGREEMENT_CONTRACT_ACTIVE',
    'AGREEMENT_PRIOR_SIGNATURE_REQUIRED',
    'AGREEMENT_INSTRUMENT_MISMATCH',
] as const;

export type PassthroughCode = (typeof PASSTHROUGH_CODES)[number];

/** What each code means to somebody holding a phone, rather than to a database. */
const CODE_MESSAGES: Record<PassthroughCode, string> = {
    AGREEMENT_CONTENT_CHANGED:
        'This document changed since you opened it. Reload and read it again before signing.',
    AGREEMENT_TYPED_NAME_REQUIRED:
        'Type your full name in the box before submitting your signature.',
    AGREEMENT_SIGN_ORDER:
        'Another party has to sign before you. You will be notified when it is your turn.',
    AGREEMENT_CONTRACT_ACTIVE:
        'This contract is already active, so its agreements can no longer be changed.',
    // NAMED AS THE DOCUMENTS CARD NAMES IT, because the next step is finding that document
    // in the list — and `DOC_TYPE_LABELS` calls it "Hire Purchase Agreement" there. It ends
    // on what happens after rather than on the refusal: this advance is sequenced behind a
    // signature, not withheld, and a signer told only "not yet" would read it as the latter.
    AGREEMENT_PRIOR_SIGNATURE_REQUIRED:
        'Your Hire Purchase Agreement has to be signed first. This advance is made in connection with it, and opens for signing once that signature is recorded.',
    // WHAT CHANGED IS THE YEAR, NOT THE DOCUMENT, so the sentence names the year and then says
    // who acts next. A signer told only that something does not match would read it as their
    // own mistake and go looking for the box they got wrong; there is no such box.
    AGREEMENT_INSTRUMENT_MISMATCH:
        'This document no longer matches the insurance year it was written for. Primelane will withdraw it and issue a corrected one, which appears here for you to sign.',
};

/** The one sentence every other failure gets. */
export const SIGNING_GENERIC_MESSAGE =
    'We could not record your signature. Please try again, or contact Primelane if it keeps happening.';

/** `CODE: sentence` — Postgres puts the code first, so the match is anchored, not a scan. */
const RAISED_CODE = /^([A-Z][A-Z0-9_]{2,63}):\s*/;

export interface SigningErrorInfo {
    code: PassthroughCode | null;
    message: string;
}

/**
 * Turns anything a signing call can reject with into `{ code, message }`.
 *
 * `code` is non-null only for the five passthrough codes, which is what lets a caller
 * react — offer a reload for a changed hash, focus the name field for a missing typed
 * name — instead of only printing a sentence.
 */
export function signingError(error: unknown): SigningErrorInfo {
    const raw =
        typeof error === 'string'
            ? error
            : ((error as { message?: string } | null)?.message ?? '');

    const explicit = (error as { code?: string } | null)?.code;
    const matched = RAISED_CODE.exec(raw.trim())?.[1] ?? explicit;

    const code = (PASSTHROUGH_CODES as readonly string[]).includes(matched ?? '')
        ? (matched as PassthroughCode)
        : null;

    return { code, message: code ? CODE_MESSAGES[code] : SIGNING_GENERIC_MESSAGE };
}

/** Convenience for a toast. */
export function signingErrorMessage(error: unknown): string {
    return signingError(error).message;
}

// ─── Draft resilience ────────────────────────────────────────────────────────

/**
 * What survives a reload mid-ceremony.
 *
 * THE DRAWN SIGNATURE IS DELIBERATELY ABSENT. Progress is worth persisting; a biometric
 * artefact sitting in localStorage after the tab closes is not. A restored draft that had
 * reached the signing step therefore lands back on the signing step with an empty pad.
 *
 * `contentSha256` is stored so a draft is discarded the moment the document underneath it
 * changes — resuming a checklist that was ticked against different text would be worse
 * than losing the progress.
 */
export interface CeremonyDraft {
    step: string;
    page: number;
    pagesSeen: number;
    readSeconds: number;
    terms: boolean[];
    typedName: string;
    contentSha256: string | null;
    savedAt: number;
}

/** A draft older than this is stale enough that re-reading is the right answer. */
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

function draftKey(scope: string): string {
    return `${ESIGN_STORAGE_PREFIX}draft:${scope}`;
}

export function loadDraft(scope: string, contentSha256: string | null): CeremonyDraft | null {
    try {
        const raw = window.localStorage.getItem(draftKey(scope));
        if (!raw) return null;
        const draft = JSON.parse(raw) as CeremonyDraft;
        if (!draft || typeof draft !== 'object') return null;
        if (Date.now() - (draft.savedAt ?? 0) > DRAFT_TTL_MS) return null;
        // A draft ticked against different bytes is not this document's draft.
        if (contentSha256 && draft.contentSha256 && draft.contentSha256 !== contentSha256) {
            return null;
        }
        return draft;
    } catch {
        return null;
    }
}

export function saveDraft(scope: string, draft: Omit<CeremonyDraft, 'savedAt'>): void {
    try {
        window.localStorage.setItem(
            draftKey(scope),
            JSON.stringify({ ...draft, savedAt: Date.now() } satisfies CeremonyDraft),
        );
    } catch {
        // A full or blocked localStorage costs progress, never the ceremony.
    }
}

export function clearDraft(scope: string): void {
    try {
        window.localStorage.removeItem(draftKey(scope));
    } catch {
        // Ignored for the same reason.
    }
}

// ─── The reader gate ─────────────────────────────────────────────────────────

/**
 * Whether a signer may leave the reader: they reached the last page AND spent the floor
 * of seconds on the step. Pure, and here rather than beside the reader component, because
 * both ceremonies use it to label their own primary button.
 */
export function readerGateOpen(
    pagesSeen: number,
    totalPages: number,
    readSeconds: number,
    secondsFloor: number,
): boolean {
    return pagesSeen >= totalPages && readSeconds >= secondsFloor;
}
